import { Toolbar } from "client/Components";
import { ReactionsButton } from "client/Features/Reactions/ReactionsButton/ReactionsButton";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { getHubUrlForCommunity } from "./CommunitiesPage";
import { HubDataSource } from "client/Api/Hub/hub";
import {
	CommunityPostStatisticsModel,
	registerReaction
} from "client/Api/Communities/communityPostViewer";
import { ReactionKind } from "client/Api/Hub/common";
import { useUnmountedCancellation } from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { isNotNil } from "client/Misc/utils";

export type CommunityPostToolbarProps = {
	statistics: CommunityPostStatisticsModel;
	onStatisticsRetrieved: (statistics: CommunityPostStatisticsModel) => void;
	postId: number;
	onComment?: () => void;
	isFollowing: boolean;
	onIsFollowingChange: (isFollowing: boolean) => void;
};

export const CommunityPostToolbar = (props: CommunityPostToolbarProps): ReactElement | null => {
	const {
		onStatisticsRetrieved,
		postId,
		statistics,
		onComment,
		isFollowing,
		onIsFollowingChange
	} = props;

	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const sendReaction = useCallback(
		(reaction: ReactionKind): Promise<CommunityPostStatisticsModel> => {
			const nextReaction: ReactionKind = reaction === statistics.currentUserReaction
				&& statistics.currentUserReaction !== ReactionKind.NoReaction
					? ReactionKind.NoReaction
					: reaction;

			return registerReaction(
				postId,
				nextReaction,
				globalCancellation
			);
		},
		[
			statistics.currentUserReaction,
			globalCancellation,
			postId
		]
	);

	const handleReactionClick = useCallback(
		async (reaction: ReactionKind): Promise<void> => {
			try {
				const statistics: CommunityPostStatisticsModel = await sendReaction(reaction);
				onStatisticsRetrieved(statistics);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				throw e;
			}
		},
		[sendReaction, onStatisticsRetrieved]
	);

	const postLink = useMemo(
		(): string => {
			const result: string = getHubUrlForCommunity({
				source: HubDataSource.Community,
				postId: postId
			});

			return result;
		},
		[postId]
	);

	if (!statistics) {
		return null;
	}

	return (
		<Toolbar
			moreButtonKind="secondary"
			trackingId="community-post-toolbar"
		>
			<Toolbar.Custom
				responsiveBehavior="always-visible"
				renderNormalMode={() => (
					<ReactionsButton
						userReaction={statistics.currentUserReaction}
						onReactionClick={handleReactionClick}
						kind="secondary"
					/>
				)}
				renderCollapsedMode={() => { throw new Error("Does not support collapse"); }}
			/>
			{isNotNil(onComment) && (
				<Toolbar.Button
					kind="secondary"
					icon="comment"
					text={localize("_Comment")}
					onClick={onComment}
					trackingId="comment"
				/>
			)}
			{isFollowing
				? (
					<Toolbar.Button
						kind="secondary"
						icon="check"
						text={localize("_Following")}
						onClick={() => onIsFollowingChange(false)}
						trackingId="unfollow"
					/>
				) : (
					<Toolbar.Button
						kind="secondary"
						icon="plus"
						text={localize("_Follow")}
						onClick={() => onIsFollowingChange(true)}
						trackingId="follow"
					/>
				)
			}
			<Toolbar.CopyButton
				value={postLink}
				kind="secondary"
				text={localize("_CopyLink")}
				icon="link"
				trackingId="copy-link"
			/>
		</Toolbar>
	);
};
CommunityPostToolbar.displayName="CommunityPostToolbar";